import React, { useCallback, useEffect, useRef, useState } from 'react';
import { getApiUrl } from '../config/api';
import Checkout from './Checkout';
import Snackbar from './Snackbar';
import { buildAgentTransactionContext } from '../utils/agentTransactionContext';
import './Renewals.css';

function formatDisplayDate(ymd) {
  if (!ymd) return '—';
  const [y, m, d] = String(ymd).slice(0, 10).split('-');
  if (!y || !m || !d) return ymd;
  return `${d}/${m}/${y}`;
}

function daysLabel(days) {
  if (days == null) return '—';
  if (days < 0) {
    const n = Math.abs(days);
    return `Expired ${n} day${n === 1 ? '' : 's'} ago`;
  }
  if (days === 0) return 'Expires today';
  if (days === 1) return 'Expires tomorrow';
  return `${days} days left`;
}

function urgencyClass(urgency) {
  if (urgency === 'overdue' || urgency === 'soon') return `renewals-urgency-${urgency}`;
  return 'renewals-urgency-upcoming';
}

function money(amount) {
  const n = Number(amount);
  if (!Number.isFinite(n)) return '—';
  return `KES ${Math.round(n).toLocaleString('en-KE')}`;
}

function Detail({ label, value }) {
  return (
    <div className="renewals-detail">
      <span>{label}</span>
      <strong>{value || '—'}</strong>
    </div>
  );
}

function RenewalLockedView({ seed, user, onBack, onPaid }) {
  const [check, setCheck] = useState({ status: 'checking', message: '' });
  const [attempt, setAttempt] = useState(0);
  const [showPayment, setShowPayment] = useState(false);
  const [notice, setNotice] = useState(null);
  const vehicle = seed.vehicle || {};
  const insured = seed.insured || {};
  const agent = seed.agent || null;
  const registration = String(vehicle.registrationNumber || '').trim();
  const resultRef = useRef(null);

  const notify = (message, type, duration) => {
    setNotice({ id: Date.now(), message, type, duration });
  };

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (!registration || !String(vehicle.chassisNumber || '').trim()) {
        const message = 'The last cover has no registration or chassis on file, so the double-insurance check cannot run.';
        setCheck({ status: 'blocked', message });
        notify(message, 'error', 12000);
        return;
      }
      setCheck({ status: 'checking', message: '' });
      notify(`Checking double insurance for ${registration}…`, 'info', 4000);
      try {
        const response = await fetch(getApiUrl('/api/dmvic/validate-double-insurance'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            vehicleData: {
              chassisNumber: vehicle.chassisNumber,
              registrationNumber: vehicle.registrationNumber
            },
            vehicleClass: seed.vehicleClass,
            policyDates: {
              startDate: seed.startDate,
              endDate: seed.endDate
            }
          })
        });
        const result = await response.json().catch(() => ({}));
        if (cancelled) return;
        if (!response.ok || result.success === false) {
          throw new Error(result.error || result.message || 'Double-insurance check failed.');
        }
        if (result.hasDoubleInsurance && result.canProceed !== true) {
          const message = result.message || 'This vehicle already has active cover for the renewal dates.';
          setCheck({ status: 'blocked', message });
          notify(`Double insurance detected for ${registration}. ${message}`, 'error', 12000);
          return;
        }
        const message = result.hasDoubleInsurance
          ? `Double insurance check passed for ${registration}. The new start date is clear.`
          : `No double insurance for ${registration}. You can continue to payment.`;
        setCheck({ status: 'ok', message });
        notify(message, 'success', 7000);
      } catch (err) {
        if (!cancelled) {
          const message = err.message || 'Double-insurance check failed.';
          setCheck({ status: 'blocked', message });
          notify(message, 'error', 12000);
        }
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [attempt, seed, registration, vehicle.chassisNumber, vehicle.registrationNumber]);

  useEffect(() => {
    if (check.status !== 'ok' && check.status !== 'blocked') return undefined;
    const frame = requestAnimationFrame(() => {
      resultRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
    });
    return () => cancelAnimationFrame(frame);
  }, [check.status, check.message]);

  const agentContext = buildAgentTransactionContext({
    agentId: agent?.id,
    agency: agent
      ? { shortDescription: agent.shortDescription || '', taxPin: agent.kraPin || '' }
      : undefined,
    issuedByAdminId: user?.id || user?.userId,
    isDirectBusiness: !agent
  });

  const insuranceNotice = notice ? (
    <Snackbar
      key={notice.id}
      message={notice.message}
      type={notice.type}
      duration={notice.duration}
      onClose={() => setNotice(null)}
    />
  ) : null;

  if (showPayment && check.status === 'ok') {
    return (
      <div className="renewals-page">
        {insuranceNotice}
        <div className="renewals-flow-head">
          <div>
            <h2 className="renewals-title">Payment for {vehicle.registrationNumber}</h2>
            <p className="renewals-subtitle">
              Cover details are locked from policy {seed.renewal?.sourcePolicyNumber || 'the last cover'}.
              Payment reserves a new policy number.
            </p>
          </div>
          <button type="button" className="renewals-back" onClick={() => setShowPayment(false)}>
            Back to renewal
          </button>
        </div>
        <Checkout
          coverType={seed.coverType}
          selectedClass={seed.vehicleClass}
          startDate={seed.startDate}
          endDate={seed.endDate}
          coverPeriod={seed.coverPeriod}
          insuredData={insured}
          vehicleData={vehicle}
          premium={{ amount: Number(seed.premiumAmount) || 0 }}
          agentId={agent?.id}
          agency={
            agent
              ? { shortDescription: agent.shortDescription || '', taxPin: agent.kraPin || '' }
              : undefined
          }
          agentTransactionContext={agentContext}
          isUnderwriting
          renewalContext={seed.renewal}
          onBack={() => setShowPayment(false)}
          onPaymentComplete={onPaid}
        />
      </div>
    );
  }

  return (
    <div className="renewals-page">
      {insuranceNotice}
      <div className="renewals-flow-head">
        <div>
          <h2 className="renewals-title">Renew {vehicle.registrationNumber || 'cover'}</h2>
          <p className="renewals-subtitle">
            This is the last issued {seed.coverType} cover. Nothing here can be changed.
            A different policy number is created at payment.
          </p>
        </div>
        <button type="button" className="renewals-back" onClick={onBack}>
          Back to renewals
        </button>
      </div>

      <div className="renewals-card">
        <h3>Last issued cover</h3>
        <div className="renewals-details">
          <Detail label="Previous policy" value={seed.renewal?.sourcePolicyNumber} />
          <Detail label="Cover" value={seed.coverType} />
          <Detail label="Vehicle class" value={seed.vehicleClass} />
          <Detail label="Period" value={seed.coverPeriod} />
          <Detail label="Previous start" value={formatDisplayDate(seed.previousStartDate)} />
          <Detail label="Previous end" value={formatDisplayDate(seed.previousEndDate)} />
          <Detail label="Last premium" value={money(seed.premiumAmount)} />
          <Detail label="Agency" value={agent?.agencyName || 'Direct business'} />
        </div>
      </div>

      <div className="renewals-card">
        <h3>Client and vehicle</h3>
        <div className="renewals-details">
          <Detail label="Insured" value={insured.insuredName} />
          <Detail label="ID number" value={insured.idNumber} />
          <Detail label="KRA PIN" value={insured.kraPin} />
          <Detail label="Phone" value={insured.phoneNumber} />
          <Detail label="Email" value={insured.emailAddress} />
          <Detail label="Registration" value={vehicle.registrationNumber} />
          <Detail label="Make" value={vehicle.make} />
          <Detail label="Model" value={vehicle.model} />
          <Detail label="Year" value={vehicle.yearOfManufacture} />
          <Detail label="Chassis" value={vehicle.chassisNumber} />
          <Detail label="Engine" value={vehicle.engineNumber} />
          <Detail label="Body" value={vehicle.bodyType} />
        </div>
      </div>

      <div className="renewals-card" ref={resultRef}>
        <h3>New term</h3>
        <div className="renewals-details">
          <Detail label="New policy number" value="Issued at payment" />
          <Detail label="Start" value={formatDisplayDate(seed.startDate)} />
          <Detail label="End" value={formatDisplayDate(seed.endDate)} />
          <Detail label="Cover" value={`${seed.coverType} · ${seed.coverPeriod}`} />
        </div>
        {check.status === 'checking' && <p className="renewals-empty">Running the double-insurance check…</p>}
        {check.status === 'ok' && (
          <p className="renewals-ok">
            Double insurance is clear. Net or Gross is chosen on the payment screen.
          </p>
        )}
        {check.status === 'blocked' && <div className="renewals-message">{check.message}</div>}
        <div className="renewals-actions">
          {check.status === 'ok' && (
            <button type="button" className="renewals-renew-btn" onClick={() => setShowPayment(true)}>
              Continue to payment
            </button>
          )}
          {check.status === 'blocked' && (
            <button type="button" className="renewals-renew-btn" onClick={() => setAttempt((n) => n + 1)}>
              Retry check
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

const Renewals = ({ user }) => {
  const [summary, setSummary] = useState({ upcoming: 0, overdue: 0, badge: 0, renewalsDone: 0 });
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [registration, setRegistration] = useState('');
  const [lookingUp, setLookingUp] = useState(false);
  const [lookupItem, setLookupItem] = useState(null);
  const [lookupError, setLookupError] = useState('');
  const [activeSeed, setActiveSeed] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const token = localStorage.getItem('adminToken');
      const res = await fetch(getApiUrl('/api/admin/renewals'), {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) {
        throw new Error(data.error || 'Could not load upcoming renewals.');
      }
      setSummary(data.summary || { upcoming: 0, overdue: 0, badge: 0, renewalsDone: 0 });
      setItems(Array.isArray(data.items) ? data.items : []);
    } catch (err) {
      setError(err.message || 'Could not load upcoming renewals.');
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleLookup = async (event) => {
    event.preventDefault();
    setLookingUp(true);
    setLookupError('');
    setLookupItem(null);
    try {
      const token = localStorage.getItem('adminToken');
      const res = await fetch(getApiUrl('/api/admin/renewals/lookup'), {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ registrationNumber: registration })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) {
        throw new Error(data.error || 'No matching cover was found.');
      }
      setLookupItem(data.item);
    } catch (err) {
      setLookupError(err.message || 'No matching cover was found.');
    } finally {
      setLookingUp(false);
    }
  };

  const startRenewal = (seed) => {
    if (!seed) return;
    setActiveSeed(seed);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (activeSeed) {
    return (
      <RenewalLockedView
        seed={activeSeed}
        user={user}
        onBack={() => setActiveSeed(null)}
        onPaid={() => load()}
      />
    );
  }

  return (
    <div className="renewals-page">
      <div className="renewals-header">
        <div>
          <h2 className="renewals-title">Renewals</h2>
          <p className="renewals-subtitle">
            Renew TPO and TOR covers on your book. Enter the vehicle registration to load the latest
            client and vehicle, run the double-insurance check, and issue a new policy number.
            A renewal can start only when the next start date is within 30 days.
          </p>
          <p className="renewals-pilot">Visible on this account only, while renewals are in pilot.</p>
        </div>
      </div>

      <div className="renewals-stats">
        <div className="renewals-stat">
          <strong>{summary.upcoming}</strong>
          <span>Expiring within 30 days</span>
        </div>
        <div className="renewals-stat">
          <strong>{summary.overdue}</strong>
          <span>Expired in the last 30 days</span>
        </div>
        <div className="renewals-stat">
          <strong>{summary.renewalsDone}</strong>
          <span>Renewals completed</span>
        </div>
      </div>

      <form className="renewals-search" onSubmit={handleLookup}>
        <input
          value={registration}
          onChange={(e) => setRegistration(e.target.value.toUpperCase())}
          placeholder="Vehicle registration, e.g. KAA123A"
          aria-label="Vehicle registration"
          autoComplete="off"
        />
        <button type="submit" disabled={lookingUp || !registration.trim()}>
          {lookingUp ? 'Searching…' : 'Find cover'}
        </button>
      </form>

      {lookupError && <div className="renewals-message">{lookupError}</div>}

      {lookupItem && (
        <div className="renewals-card">
          <h3>
            {lookupItem.registrationNumber} · {lookupItem.coverType}
          </h3>
          <p className="renewals-meta">
            {lookupItem.insuredName || 'Client'} · {lookupItem.vehicleClass || 'Vehicle class'}
            <br />
            Policy {lookupItem.policyNumber || '—'} · cover ends {formatDisplayDate(lookupItem.currentEndDate)}
            <br />
            {lookupItem.agencyName ? `Agency: ${lookupItem.agencyName}` : 'Direct business'}
            {lookupItem.daysLeft != null ? ` · ${daysLabel(lookupItem.daysLeft)}` : ''}
          </p>
          {lookupItem.eligible ? (
            <div className="renewals-actions">
              <button type="button" className="renewals-renew-btn" onClick={() => startRenewal(lookupItem.seed)}>
                Renew
              </button>
              <span className="renewals-meta">
                Opens the last issued cover. Details stay locked, and payment creates a new policy number.
              </span>
            </div>
          ) : (
            <div className="renewals-message">{lookupItem.reason}</div>
          )}
        </div>
      )}

      <div className="renewals-table-wrap">
        <h3>Upcoming renewals</h3>
        {error && <div className="renewals-message">{error}</div>}
        {loading ? (
          <p className="renewals-empty">Loading your TPO and TOR book…</p>
        ) : items.length === 0 ? (
          <p className="renewals-empty">
            No TPO or TOR cover on your book is inside the 30-day renewal window.
          </p>
        ) : (
          <table className="renewals-table">
            <thead>
              <tr>
                <th>Registration</th>
                <th>Client</th>
                <th>Cover</th>
                <th>Policy</th>
                <th>Ends</th>
                <th>Window</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr key={item.clientId}>
                  <td>{item.registrationNumber || '—'}</td>
                  <td>
                    {item.insuredName || '—'}
                    <div className="renewals-meta">{item.agencyName || 'Direct business'}</div>
                  </td>
                  <td>
                    {item.coverType}
                    <div className="renewals-meta">{item.vehicleClass}</div>
                  </td>
                  <td>{item.policyNumber || '—'}</td>
                  <td>{formatDisplayDate(item.currentEndDate)}</td>
                  <td className={urgencyClass(item.urgency)}>{daysLabel(item.daysLeft)}</td>
                  <td>
                    <button
                      type="button"
                      className="renewals-renew-btn"
                      disabled={!item.seed}
                      onClick={() => startRenewal(item.seed)}
                    >
                      Renew
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default Renewals;
